<template>
  <div class="tw-flex tw-gap-sm tw-flex-col tw-items-end">
    <div class="tw-flex tw-gap-lg tw-items-center">
      <div class="tw-flex tw-flex-col tw-items-end tw-gap-xs">
        <ParserHelpLink />
      </div>
      <component :is="timesheetParsingStore.uploadedFiles.length > 1 ? WdsTooltip : 'div'" left>
        <template #tooltip-content>
          <div class="tw-flex tw-flex-col tw-items-start">
            <div v-for="file in timesheetParsingStore.uploadedFiles" :key="file.localId">
              {{ file.name }}
            </div>
          </div>
        </template>

        <ModalAiConsent
          v-model:show="showConsentModal"
          @ai-terms-agreement-agreed="openFilePicker"
        />

        <WdsButton
          title=""
          class="worksome-intelligence-border tw-whitespace-nowrap"
          lg
          secondary
          data-test="timesheet-parsing-upload-button"
          :disabled="operationInProgress"
          @click="handleUploadButtonClick"
        >
          <WdsStack align-center gap-sm>
            <WdsAvatar
              v-if="operationInProgress"
              path="/gifs/worksome-assistant-thinking.gif"
              xs
              class="max-w-[16px]"
            />
            <WdsIcon
              v-else-if="timesheetParsingStore.uploadedFiles.length === 0"
              :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT"
              xs
              :size="16"
            />
            <WdsIcon
              v-else
              :name="WdsIconNameEnum.DOCUMENT"
              xs
              :size="16"
              class="tw-text-indigo-400"
            />
            <span
              v-if="
                timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.DELETING
              "
            >
              {{
                $tc('Deleting file | Deleting files', timesheetParsingStore.uploadedFiles.length)
              }}...
            </span>
            <span
              v-if="
                timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.UPLOADING
              "
            >
              {{
                $tc(
                  'Processing file | Processing files',
                  timesheetParsingStore.uploadedFiles.length,
                )
              }}...
            </span>
            <span
              v-if="
                timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.EXTRACTING
              "
            >
              {{ $t('Extracting data') }}...
            </span>
            <span
              v-if="timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.CLEAN"
            >
              {{ $t('Auto-fill') }}
            </span>
            <span
              v-if="timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.PARSED"
            >
              {{
                timesheetParsingStore.uploadedFiles.length > 1
                  ? $tc(
                      'Auto-filled (:count file) | Auto-filled (:count files)',
                      timesheetParsingStore.uploadedFiles.length,
                      { count: timesheetParsingStore.uploadedFiles.length },
                    )
                  : truncatedFileName
              }}
            </span>
            <WdsIcon
              v-if="
                !operationInProgress &&
                timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.CLEAN
              "
              :name="WdsIconNameEnum.UPLOAD"
              :size="16"
            />
          </WdsStack>
        </WdsButton>
      </component>
    </div>
    <div
      v-if="
        timesheetParsingStore.uploadedFiles.length > 0 &&
        timesheetParsingStore.currentStep === timesheetParsingStore.ProcessStep.PARSED
      "
      class="tw-flex tw-gap-sm tw-text-sm tw-items-center tw-justify-end"
    >
      <span>
        {{
          $tc(
            'Attach file to the Request | Attach files to the Request',
            timesheetParsingStore.uploadedFiles.length,
          )
        }}
      </span>
      <WdsToggle v-model:checked="timesheetParsingStore.attachFilesToRequest" />
    </div>
  </div>
  <ProvideApiFilebank
    v-slot="{ uploadFiles: fileUploader, deleteFiles: fileDeleter }"
    :default-tags="['parsed-timesheet']"
    @uploaded="handleRecentlyUploadedFiles"
    @update-busy-status="workingAsReportedByFileBank = $event"
  >
    <input
      ref="fileinput"
      type="file"
      multiple
      accept="image/png,image/jpeg,application/pdf"
      style="display: none"
      @change="handleFileChange($event, fileUploader, fileDeleter)"
    />
  </ProvideApiFilebank>
</template>

<script setup lang="ts">
import {
  WdsAvatar,
  WdsButton,
  WdsIcon,
  WdsIconNameEnum,
  WdsStack,
  WdsToggle,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed, ref, onMounted } from 'vue'
import { useTimesheetFormStore } from './store/useTimesheetFormStore'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'
import { useTranslation } from '@/composables/translation'
import { Message } from '@/utils/notification'
import { useTimesheetParsingStore } from '@/components/Timesheet/store/useTimesheetParsingStore'
import goPost from '@/utils/goPost'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import ModalAiConsent from '@/components/LLM/ModalAiConsent.vue'
import { useAiConsent } from '@/composables/useAiConsent'
import ParserHelpLink from '@/components/Timesheet/ParserHelpLink.vue'

const props = defineProps<{
  openFilePickerOnMount?: boolean
}>()

const fileinput = ref<HTMLInputElement | null>(null)
const openFilePicker = () => fileinput.value?.click()

const { $t, $tc } = useTranslation()
const timesheetParsingStore = useTimesheetParsingStore()
const timesheetFormStore = useTimesheetFormStore()
const paymentRequestStore = usePaymentRequestStore()
const workingAsReportedByFileBank = ref(false)

const { needsConsent, showConsentModal } = useAiConsent()

const handleUploadButtonClick = () => {
  if (needsConsent.value) {
    showConsentModal.value = true
  } else {
    openFilePicker()
  }
}

onMounted(() => {
  if (props.openFilePickerOnMount) {
    handleUploadButtonClick()
  }
})

interface UploadedFile {
  name: string
  localId: string
  size?: number
}

interface TimesheetEntry {
  type: 'day'
  date: string
  duration: number
  unit: 'hours' | 'days'
}

const handleRemoveFiles = async (fileDeleter: (ids: string[]) => Promise<any>): Promise<void> => {
  const files = timesheetParsingStore.uploadedFiles.map((file) => file.localId)
  if (!files.length) return
  if (files) {
    await fileDeleter(files)
  }
}

const handleFileChange = async (
  event: Event,
  fileUploader: (files: FileList) => Promise<any>,
  fileDeleter: (ids: string[]) => Promise<any>,
): Promise<void> => {
  const target = event.target as HTMLInputElement
  const files = target.files || (event as DragEvent).dataTransfer?.files

  if (files) {
    await handleRemoveFiles(fileDeleter)
    timesheetParsingStore.rollbackParsedTimesheetRegistrations()
    timesheetParsingStore.currentStep = timesheetParsingStore.ProcessStep.UPLOADING
    await fileUploader(files)
  }
}

const handleRecentlyUploadedFiles = async (
  recentlyUploadedFiles: UploadedFile[],
  fileDeleter: (ids: string[]) => Promise<any>,
): Promise<void> => {
  timesheetParsingStore.uploadedFiles = [...(recentlyUploadedFiles || [])]
  timesheetParsingStore.currentStep = timesheetParsingStore.ProcessStep.EXTRACTING
  const response = await goPost(laravelNamedRoute(namedRoutes.api.ai['parse-timesheet']), {
    hire_id: paymentRequestStore.hireLocalId,
    file_ids: recentlyUploadedFiles.map((file) => file.localId),
  })
  // const response = await new Promise<TimesheetEntry[]>((resolve) => {
  //   setTimeout(() => {
  //     resolve([
  //       {
  //         type: 'day',
  //         date: '2025-05-19',
  //         duration: 8,
  //         unit: 'hours',
  //       },
  //       {
  //         type: 'day',
  //         date: '2025-05-20',
  //         duration: 8,
  //         unit: 'hours',
  //       },
  //       {
  //         type: 'day',
  //         date: '2025-05-21',
  //         duration: 4.25,
  //         unit: 'hours',
  //       },
  //       {
  //         type: 'day',
  //         date: '2025-05-22',
  //         duration: 8,
  //         unit: 'hours',
  //       },
  //       {
  //         type: 'day',
  //         date: '2025-06-23',
  //         duration: 8,
  //         unit: 'hours',
  //       },
  //     ])
  //   }, 500)
  // })
  if (response.length > 0) {
    timesheetParsingStore.hydrateFromParserRegistrations(response)
    timesheetParsingStore.currentStep = timesheetParsingStore.ProcessStep.PARSED
    if (timesheetParsingStore.countOfValidParsedRegistrationsToBeFilled > 0) {
      timesheetFormStore.detailedView = false
    } else {
      timesheetParsingStore.uploadedFiles = []
      timesheetParsingStore.currentStep = timesheetParsingStore.ProcessStep.CLEAN
    }
  } else {
    timesheetParsingStore.rollbackParsedTimesheetRegistrations()
    timesheetParsingStore.currentStep = timesheetParsingStore.ProcessStep.CLEAN
    new Message($t('Failed to extract valid timesheet data')).dispatch()
  }
}

const handleDeletedFiles = (deletedFiles: UploadedFile[]): void => {
  if (deletedFiles.length > 0) {
    timesheetParsingStore.rollbackParsedTimesheetRegistrations()
    if (fileinput.value) {
      fileinput.value.value = ''
    }
    timesheetParsingStore.currentStep = timesheetParsingStore.ProcessStep.CLEAN
  }
}

const truncatedFileName = computed(() => {
  const name = timesheetParsingStore.uploadedFiles?.[0]?.name || ''
  const lastDot = name.lastIndexOf('.')
  if (lastDot === -1) return name

  const base = name.slice(0, lastDot)
  const ext = name.slice(lastDot)
  const maxBaseLength = 12 // Adjust as needed

  return base.length > maxBaseLength ? `${base.slice(0, maxBaseLength)}..${ext}` : name
})

const operationInProgress = computed(
  () => timesheetParsingStore.operationInProgress || workingAsReportedByFileBank.value,
)
</script>

<style scoped>
:deep(.wds-circle-container) {
  max-width: 16px;
  max-height: 16px;
}
:deep(.wds-toggle-wrapper .wds-toggle) {
  display: flex;
}

.worksome-intelligence-border {
  position: relative;
  border: none !important;
}

.worksome-intelligence-border::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 4px;
  padding: 1px;
  background: linear-gradient(
    53.76deg,
    #634cee 9.2%,
    rgba(99, 76, 238, 0.94) 26.8%,
    #f15c30 60.32%,
    #ffc800 93.01%
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}
</style>
